import { createFileRoute, Link } from "@tanstack/react-router";
import { TackLogo } from "@/components/TackLogo";
import { SiteFooter } from "@/components/SiteFooter";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Tack. Terms & Conditions" },
      { name: "description", content: "Terms and conditions for Tack, a customer loyalty and retention SaaS platform for local businesses by Lineri AB." },
      { property: "og:title", content: "Tack. Terms & Conditions" },
      { property: "og:description", content: "Terms for using Tack, a customer loyalty and retention platform by Lineri AB." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://tack.company/terms" },
    ],
    links: [{ rel: "canonical", href: "https://tack.company/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <Link to="/" aria-label="Tack home" className="inline-block hover:opacity-80 transition-opacity">
            <TackLogo size="md" />
          </Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-display font-bold mb-8">Terms & Conditions</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: {new Date().toLocaleDateString("en-SE")}</p>

        <div className="prose prose-sm max-w-none space-y-6 text-foreground/90 leading-relaxed">
          <section>
            <h2 className="text-xl font-display font-semibold mb-3">1. About Us</h2>
            <p>Tack is a customer retention platform operated by <strong>Lineri AB</strong> (trading as "Tack."), a company registered in Sweden. By using Tack, you are entering into an agreement with Lineri AB.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">2. Acceptance of Terms</h2>
            <p>By accessing or using Tack, you agree to be bound by these Terms & Conditions. If you do not agree, please do not use the service. Continued use of the service constitutes acceptance of any updates to these terms.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">3. Service Description</h2>
            <p>Tack provides QR-code-based customer feedback collection, discount code generation, and retention analytics for small businesses. Features vary by subscription tier.</p>
            <p className="mt-2">Tack is currently offered to merchants located in Sweden. SMS delivery is optimized for Swedish mobile numbers. We plan to expand availability to additional markets over time.</p>
            <p className="mt-2"><strong>Product category.</strong> Tack is a customer loyalty and retention platform for local businesses (SaaS). Transactional SMS is used solely to deliver the customer's own one-time discount code and, optionally, a reminder before that code expires, under consent captured at the moment of interaction with the merchant's QR flow. Tack is not a bulk-messaging service, list-marketing service, cold-outreach tool, or lead-generation service, and merchants may not upload or import external contact lists.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">4. Account & Credentials</h2>
            <p>You must provide accurate information when creating an account and keep your login credentials confidential. You are responsible for all activity under your account. If you represent a business, you confirm you have authority to bind that organisation.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">5. Acceptable Use</h2>
            <p>You must not misuse Tack. Prohibited activities include, but are not limited to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Using the service for any unlawful purpose</li>
              <li>Sending spam or fraudulent communications</li>
              <li>Sending unsolicited marketing, or contacting phone numbers not collected through the Tack QR flow</li>
              <li>Uploading, importing, or messaging purchased, scraped, or otherwise externally sourced contact lists</li>
              <li>Using Tack for any form of bulk or mass marketing, cold outreach, or lead generation</li>
              <li>Infringing on intellectual property rights</li>
              <li>Attempting to interfere with the security or integrity of the service (including malware, scraping, or probing)</li>
              <li>Reverse engineering, reselling, or redistributing the service</li>
            </ul>
            <p className="mt-2">Violation of the messaging-use restrictions above results in immediate suspension.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">6. Merchant Responsibilities</h2>
            <p>As a merchant using Tack, you are solely responsible for:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Honouring the rewards, discounts, and offers you configure.</strong> Tack issues codes on your behalf based on the offer you set up. Disputes between you and your customers about whether a code was honoured, the value of the reward, or the terms of redemption are between you and the customer. Lineri AB is not party to that transaction and accepts no liability for refused, expired, or unfulfilled rewards.</li>
              <li><strong>The accuracy of your business information</strong>, including offer terms, redemption rules, expiry windows, and any restrictions you advertise on the customer-facing scan page.</li>
              <li><strong>Compliance with consumer protection law in your jurisdiction</strong>, including any rules on advertised discounts, voucher expiry, and minimum spend conditions.</li>
              <li><strong>Communications you send to your customers via Tack</strong> (We Miss You nudges, custom messages). You are the sender of record; Tack is the delivery infrastructure.</li>
              <li><strong>Your use of customer personal data collected via Tack.</strong> You act as a joint data controller for customer email addresses collected at your location, and must handle them lawfully. Our <Link to="/dpa" className="text-primary underline">Data Processing Agreement</Link> governs this relationship.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">7. Intellectual Property</h2>
            <p>All rights, title, and interest in and to Tack — including software, documentation, branding, and design — are and remain the property of Lineri AB. You are granted a limited, non-exclusive, non-transferable right to use the service within the scope of your selected plan.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">8. User Content & Customer Disputes</h2>
            <p>You retain ownership of any content you submit through Tack. By submitting content, you grant Lineri AB a limited licence to host and process it solely to provide the service.</p>
            <p className="mt-2">If a customer raises a complaint about a reward, redemption, or your service, you agree to handle it directly. If the complaint is escalated to us, we may at our discretion forward it to you and require a reasonable response. Repeated unresolved complaints may lead to account suspension under section 11.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">9. Payment & Subscriptions</h2>
            <p>Tack offers paid subscription plans billed monthly or annually. First-time subscribers get a 30-day free trial, which begins when you pick a plan and add a payment method. No charge is made until the trial ends. Cancellations and refunds are governed by our <Link to="/refund" className="text-primary underline">Refund Policy</Link>.</p>
            <p className="mt-2">Prices displayed on our website and in the app are quoted in Swedish kronor (SEK) and are exclusive of VAT, which is calculated and added at checkout based on your location. EU businesses may enter a valid VAT number at checkout to apply the reverse charge mechanism.</p>
            <p className="mt-2"><strong>Lineri AB (Tack)</strong> is the seller for all subscription plans and for the physical starter kit. Payments are processed by <strong>Stripe</strong>, which also calculates and collects VAT, issues invoices and receipts, and handles payment-level disputes and fraud checks on our behalf. Product questions, service issues, and starter kit delivery and returns are handled directly by us.</p>
            <p className="mt-2">Subscription charges appear on your bank or card statement as <strong>LINK.COM* TACK</strong>. Starter kit and one-off charges appear as <strong>TACK.COMPANY</strong>.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">10. Service Availability</h2>
            <p>We strive to keep Tack available at all times but do not guarantee uninterrupted or error-free performance. We may perform maintenance that temporarily affects availability.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">11. Suspension & Termination</h2>
            <p>We may suspend or terminate your access to Tack if you:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Materially breach these terms</li>
              <li>Fail to make payment</li>
              <li>Pose a security or fraud risk</li>
              <li>Repeatedly or seriously violate our policies</li>
            </ul>
            <p className="mt-2">Upon termination, your right to use the service ceases immediately. We may retain your data for a reasonable period to allow export, after which it will be deleted.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">12. Limitation of Liability</h2>
            <p>To the fullest extent permitted by law, Lineri AB disclaims all implied warranties, including merchantability and fitness for a particular purpose. Our aggregate liability is capped at the fees you have paid in the preceding 12 months. We are not liable for indirect, consequential, or special damages, including loss of profits, data, or goodwill.</p>
            <p className="mt-2">In particular, our liability does not extend to the value of any reward, discount, or offer issued via Tack on your behalf.</p>
            <p className="mt-2">Nothing in these terms excludes liability for fraud, death, or personal injury caused by negligence.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">13. Indemnification</h2>
            <p>You agree to indemnify Lineri AB against any claims arising from your content, unlawful use, or violation of these terms.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">14. Governing Law</h2>
            <p>These terms are governed by the laws of Sweden. Any disputes shall be resolved by the courts of Sweden.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">15. Changes to Terms</h2>
            <p>We may update these terms from time to time. We will notify you of material changes via email or in-app notification. Continued use after changes constitutes acceptance.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">16. Contact</h2>
            <p>For questions about these terms, contact us at <a href="mailto:hello@tack.company" className="text-primary underline">hello@tack.company</a>.</p>
          </section>
        </div>
      </main>

      <SiteFooter variant="legal" />
    </div>
  );
}
